<script lang="ts">
import { defineComponent, PropType, toRef, computed } from 'vue'
import { formDefaultProps, formPartial } from '@/models/formPartial'

export default defineComponent({
  name: 'InputLeadFormWrapper',

  props: {
    ...formDefaultProps,

    modelValue: {
      type: [String, Number, Boolean, Array] as PropType<InputValue>,
      default: undefined,
    },

    /**
     * 選択タイプのフォームかどうか
     */
    isSelectType: {
      type: Boolean,
      default: false,
    },

    /**
     * 見出しや説明文のサイズ感
     */
    size: {
      type: String as PropType<'small' | 'normal' | 'large'>,
      default: 'normal',
    },
  },

  emits: ['error'],

  setup(props) {
    // model
    const { hasError, isRequired, createErrorMessage } = formPartial(props.name, toRef(props, 'validationScheme'))

    /**
     * createErrorMessage に渡す引数用
     *
     * @return
     *  [
     *    isSingleSelectType 対象が単一選択式のフォームかどうか,
     *    isMultiSelectType 対象が複数選択式のフォームかどうか,
     *    prefix エラーメッセージの接頭に来る見出し文言,
     *  ]
     * @see app/frontend/models/formPartial.ts
     */
    const messageType = computed<[boolean, boolean, string]>(() => [
      props.isSelectType && !Array.isArray(props.modelValue),
      props.isSelectType && Array.isArray(props.modelValue),
      props.header || '',
    ])

    return {
      // computed
      hasError,
      isRequired,
      messageType,

      // methods
      createErrorMessage,
    }
  }
})
</script>

<template>
  <dl class="dl">
    <dt
      v-if="header || isRequired"
      :class="[
        'dt',
        'u-text__bold',
        { 'u-text__x-small__fixed': size === 'small' },
        { 'u-text__small__fixed': size === 'normal' },
        { 'u-text__normal__fixed': size === 'large' },
      ]">
      <span
        class="span"
        v-html="header" />
      <span
        v-if="isRequired"
        class="span u-text__required">
        必須
      </span>
      <p
        v-if="description"
        :class="[
          'p',
          'u-margin__smaller',
          'u-text__weak',
          { 'u-text__small': size === 'small' },
          { 'u-text__large': size === 'large' },
        ]">
        {{ description }}
      </p>
    </dt>

    <dd class="dd">
      <ValidationProvider
        :value="modelValue"
        :namespace="namespace"
        :field="name"
        :scheme="validationScheme"
        :messages="createErrorMessage(...messageType)"
        @error="$emit('error', $event)">
        <slot />

        <template #secondary>
          <slot name="secondary" />
        </template>
      </ValidationProvider>
    </dd>
  </dl>
</template>
